import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { asyncScheduler, BehaviorSubject, Subject, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '~environments/environment';
import { take, tap, throttleTime } from 'rxjs/operators';

export interface LmoDriverPageParameters {
  searchString?: string;
  sortBy?: string;
  sortDirection?: string;
  carrierId?: number;
  subcarrierId?: number;
  pageNumber: number;
  pageSize: number;
}

export interface LmoDriver {
  showDetails: boolean;
  id: number;
  name: string;
  phone: string;
  carrierId: number;
  carrier: string;
  subcarrier: string;
  validCdl: boolean;
  banId: number;
  banReason: string;
  bannedAt: string;
  bannedBy: string;
  canBeBanned: boolean;
  siteRestrictions: Record<number, DriverSiteRestriction>;
}

export interface LmoDriverPage {
  drivers: LmoDriver[];
  total: number;
  pageNumber: number;
  pageSize: number;
  loading: boolean;
}

export interface LmoBannedDriver {
  banId: number;
  name: string;
  phone: string;
  carrier: string;
  subcarrier: string;
  banReason: string;
  bannedAt: string;
  bannedBy: string;
}

export interface LmoBannedDriverPage {
  drivers: LmoBannedDriver[];
  total: number;
  pageNumber: number;
  pageSize: number;
  loading: boolean;
}

interface LmoDriverResponse {
  drivers: LmoDriver[];
  total: number;
  pageNumber: number;
  pageSize: number;
}

interface LmoBannedDriverResponse {
  drivers: LmoBannedDriver[];
  total: number;
  pageNumber: number;
  pageSize: number;
}

export interface DriverSiteRestriction {
  id: number;
  siteId: number;
  siteName: string;
  banReason: string;
  createdAt: string;
  isArchived: boolean;
}

export interface LmoCarriersAndSubcarriers {
  carrier_id: number;
  carrier_name: string;
  subcarrier_id: number;
  subcarrier_name: string;
}

interface SearchbarValueResponse {
  searchDriver: string;
  searchBanDriver: string;
}

@Injectable({
  providedIn: 'root',
})
export class LmoDriverService implements OnDestroy {
  private lmoDriversParameters$$ = new BehaviorSubject<LmoDriverPageParameters>({ pageNumber: 1, pageSize: 10 });
  private lmoDriversPage$$ = new BehaviorSubject<LmoDriverPage>({
    drivers: [],
    total: 0,
    pageNumber: 1,
    pageSize: 10,
    loading: true,
  });
  public lmoDriversPage$ = this.lmoDriversPage$$.asObservable();

  private lmoBannedDriversParameters$$ = new BehaviorSubject<LmoDriverPageParameters>({ pageNumber: 1, pageSize: 10 });
  private lmoBannedDriversPage$$ = new BehaviorSubject<LmoBannedDriverPage>({
    drivers: [],
    total: 0,
    pageNumber: 1,
    pageSize: 10,
    loading: true,
  });
  public lmoBannedDriversPage$ = this.lmoBannedDriversPage$$.asObservable();

  private carriersForDriverLMO$$ = new BehaviorSubject<LmoCarriersAndSubcarriers[]>([]);
  public carriersForDriverLMO$ = this.carriersForDriverLMO$$.asObservable();
  private subcarriersForDriverLMO$$ = new BehaviorSubject<LmoCarriersAndSubcarriers[]>([]);
  public subcarriersForDriverLMO$ = this.subcarriersForDriverLMO$$.asObservable();

  public searchbarValue$$ = new BehaviorSubject<SearchbarValueResponse>({
    searchDriver: '',
    searchBanDriver: '',
  });

  private subs: Subscription[] = [];

  get route(): string {
    return environment.api + '/lmo/driver';
  }

  constructor(private http: HttpClient, private snackbar: MatSnackBar) {
    this.subs.push(
      this.lmoDriversParameters$$
        .pipe(throttleTime(1000, asyncScheduler, { leading: true, trailing: true }))
        .subscribe((params) => {
          this.getLmoDrivers();
        }),
    );

    this.subs.push(
      this.lmoBannedDriversParameters$$
        .pipe(throttleTime(1000, asyncScheduler, { leading: true, trailing: true }))
        .subscribe((params) => {
          this.getLmoBannedDrivers();
        }),
    );
  }

  public ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  public getLmoDrivers() {
    const parameters = this.lmoDriversParameters$$.value;
    this.http.post<LmoDriverResponse>(`${this.route}/all`, parameters).subscribe(
      (response) => {
        this.lmoDriversPage$$.next({
          drivers: response?.drivers || [],
          total: response?.total || 0,
          pageNumber: response?.pageNumber || 1,
          pageSize: response?.pageSize || 10,
          loading: false,
        });
      },
      (error) => {
        this.snackbar.open('Error getting drivers', null, { duration: 3000, panelClass: ['snackbar-error'] });
      },
    );
  }

  public getLmoBannedDrivers() {
    const parameters = this.lmoBannedDriversParameters$$.value;
    this.http.post<LmoBannedDriverResponse>(`${this.route}/banned`, parameters).subscribe(
      (response) => {
        this.lmoBannedDriversPage$$.next({
          drivers: response?.drivers || [],
          total: response?.total || 0,
          pageNumber: response?.pageNumber || 1,
          pageSize: response?.pageSize || 10,
          loading: false,
        });
      },
      (error) => {
        this.snackbar.open('Error getting banned drivers', null, { duration: 3000, panelClass: ['snackbar-error'] });
      },
    );
  }

  public setLmoDriversPage(pageNumber: number, pageSize: number) {
    this.lmoDriversParameters$$.next({ ...this.lmoDriversParameters$$.value, pageNumber, pageSize });
  }

  public setLmoDriversSort(sortBy: string, sortDirection: string) {
    this.lmoDriversParameters$$.next({ ...this.lmoDriversParameters$$.value, pageNumber: 1, sortBy, sortDirection });
  }

  public setLmoDriversSearch(searchString: string) {
    this.lmoDriversParameters$$.next({ ...this.lmoDriversParameters$$.value, pageNumber: 1, searchString });
  }

  public setLmoDriversSearchCarries(carrierId: number) {
    this.lmoDriversParameters$$.next({ ...this.lmoDriversParameters$$.value, pageNumber: 1, carrierId });
  }

  public setLmoDriversSearchSubcarries(subcarrierId: number) {
    this.lmoDriversParameters$$.next({ ...this.lmoDriversParameters$$.value, pageNumber: 1, subcarrierId });
  }

  public setLmoBannedDriversPage(pageNumber: number, pageSize: number) {
    this.lmoBannedDriversParameters$$.next({ ...this.lmoBannedDriversParameters$$.value, pageNumber, pageSize });
  }

  public setLmoBannedDriversSort(sortBy: string, sortDirection: string) {
    this.lmoBannedDriversParameters$$.next({
      ...this.lmoBannedDriversParameters$$.value,
      pageNumber: 1,
      sortBy,
      sortDirection,
    });
  }

  public setLmoBannedDriversSearch(searchString: string) {
    this.lmoBannedDriversParameters$$.next({ ...this.lmoBannedDriversParameters$$.value, pageNumber: 1, searchString });
  }

  public resetLmoDriversParameters() {
    this.lmoDriversParameters$$.next({ pageNumber: 1, pageSize: 10 });
  }

  public resetLmoBannedDriversParameters() {
    this.lmoBannedDriversParameters$$.next({ pageNumber: 1, pageSize: 10 });
  }

  public async banDriver(driverId: number, banReason: string): Promise<boolean> {
    try {
      await this.http
        .post(`${this.route}/ban`, { driverId, banReason })
        .pipe(take(1))
        .toPromise();

      this.snackbar.open('Driver banned', null, { duration: 3000, panelClass: ['snackbar-success'] });
      this.getLmoDrivers();
      this.getLmoBannedDrivers();

      return true;
    } catch (error) {
      this.snackbar.open('Error suspending driver', null, { duration: 3000, panelClass: ['snackbar-error'] });

      return false;
    }
  }

  public async unbanDriver(banId: number, unbanReason: string): Promise<boolean> {
    try {
      await this.http
        .post(`${this.route}/unban`, { banId, unbanReason })
        .pipe(take(1))
        .toPromise();

      this.snackbar.open('Driver reinstated', null, { duration: 3000, panelClass: ['snackbar-success'] });
      this.getLmoDrivers();
      this.getLmoBannedDrivers();

      return true;
    } catch (error) {
      this.snackbar.open('Error reinstating driver', null, { duration: 3000, panelClass: ['snackbar-error'] });

      return false;
    }
  }

  public async banDriverFromSite(driverId: number, selectedSites: any[]): Promise<boolean> {
    try {
      await this.http
        .post(`${this.route}/banDriverFromSite`, { driverId, selectedSites })
        .pipe(take(1))
        .toPromise();

      const bannedSitesNames = selectedSites.map((site) => site.siteName).join(', ');
      this.snackbar.open(`Driver banned from sites: ${bannedSitesNames}`, null, {
        duration: 3000,
        panelClass: ['snackbar-success'],
      });

      return true;
    } catch (error) {
      this.snackbar.open('Error suspending driver from sites', null, {
        duration: 3000,
        panelClass: ['snackbar-error'],
      });
      return false;
    }
  }

  public async unbanDriverFromSite(id: number): Promise<boolean> {
    try {
      await this.http.post(`${this.route}/unbanDriverFromSite`, { banId: id }).toPromise();
      this.snackbar.open('Driver unbanned from site', null, { duration: 3000, panelClass: ['snackbar-success'] });
      return true;
    } catch (error) {
      console.error('Error unbanning driver:', error);
      this.snackbar.open('Error unbanning driver from site', null, { duration: 3000, panelClass: ['snackbar-error'] });
      return false;
    }
  }

  public getLmoDriverCarriersAndSubcarriers() {
    this.http
      .get<LmoCarriersAndSubcarriers[]>(this.route + '/carriersAndSubcarriers')
      .subscribe((carriersAndSubcarriers: LmoCarriersAndSubcarriers[]) => {
        // Distinct carriers
        const distinctCarriers = carriersAndSubcarriers.reduce((acc, curr) => {
          const existingCarrier = acc.find((c) => c.carrier_id === curr.carrier_id);
          if (!existingCarrier) {
            acc.push(curr);
          }
          return acc;
        }, []);

        // Distinct subcarriers
        const distinctSubcarriers = carriersAndSubcarriers.reduce((acc, curr) => {
          if (curr.subcarrier_id && !acc.some((s) => s.subcarrier_id === curr.subcarrier_id)) {
            acc.push(curr);
          }
          return acc;
        }, []);

        this.carriersForDriverLMO$$.next(distinctCarriers);
        this.subcarriersForDriverLMO$$.next(distinctSubcarriers);
      });
  }
}
